import React, { memo, useState, useEffect, useCallback, useRef, useMemo } from "react";
import styles from "./SolarRecommendation.module.scss";
import { useNavigate } from "react-router-dom";
import TabViewer from "../../../common/components/tab-viewer/TabViewer";
import useWindowDimensions, { CurrentScreen } from "../../../hooks/use-window-dimensions";
import { ApplianceItem } from "../../../model/solar-recommendation-form/appliance-item";
import { Installation } from "../../../model/solar-recommendation-form/installation";
import { PaymentPlan } from "../../../model/solar-recommendation-form/paymentPlan";
import { SolarRecommendationForm } from "../../../model/solar-recommendation-form/solar-recommendation-form";
import { ApplicationRoutes } from "../../../routes";
import AddAppliancesForm from "./components/add-appliances/AddAppliancesForm";
import InstallationForm from "./components/installation/InstallationForm";
import PaymentPlanForm from "./components/payment-plan/PaymentPlanForm";
import PreviewSection from "./components/preview-section/PreviewSection";
import SolarRecommendationFormWrapper from "../../sign-up/wrapper/SolarRecommendationFormWrapper";
import { toast } from "react-toastify";
import SolarRecommendationFinalization from "./SolarRecommendationFinalization";

type Props = {};

const SolarRecommendation: React.FC<Props> = memo((props: Props) => {
	const formFromStorage = sessionStorage.getItem("solar-recommendation-form");

	const parsedForm: SolarRecommendationForm | undefined = formFromStorage ? JSON.parse(formFromStorage) : undefined;

	const loadedFinishedSteps = loadFinishedStepsFromForm(parsedForm);

	const [form, setForm] = useState<SolarRecommendationForm>(parsedForm ?? new SolarRecommendationForm({ appliances: [] }));

	const currentScreen: CurrentScreen = useWindowDimensions();

	const [currentStep, setCurrentStep] = useState<number>(1);
	const [finishedSteps, setFinishedSteps] = useState<number[]>(loadedFinishedSteps);
	const appliancesRef = useRef<{ saveAndContinue: () => void } | null>(null);
	const paymentRef = useRef<{ savePayment: () => void } | null>(null);

	const navigate = useNavigate();

	const finalize = useCallback(() => {
		// navigate(ApplicationRoutes.RECOMMENDATION_FINALIZATION);
	}, [navigate]);

	const handleNext = useCallback(() => {
		if (currentStep === 1 && appliancesRef.current) {
			appliancesRef?.current.saveAndContinue();
		} else if (currentStep === 2) {
			paymentRef?.current?.savePayment();
		} else if (currentStep === 3) {
			// Handle next for Installation
			setCurrentStep(4);
		}
	}, [currentStep]);

	const wrapperObject: {
		title: string;
		labelNext?: string;
		labelNextStep: string;
		labelBackStep: string;
		showDesktopNext?: boolean;
		showMobileNext?: boolean;
		showDesktopBack?: boolean;
		showMobileBack?: boolean;
	} = useMemo(() => {
		switch (currentStep) {
			case 1:
				return {
					title: "Add Appliance",
					labelNextStep: "Payment Information",
					labelBackStep: "Basic Information",
					showDesktopBack: false,
					showDesktopNext: false,
				};
			case 2:
				return { title: "Payment Information", labelBackStep: "Appliances", labelNextStep: "Installation", showDesktopBack: false, showDesktopNext: false };
			case 3:
				return {
					title: "Installation",
					labelBackStep: "Payment Information",
					labelNextStep: "Finish Setup",
					labelNext: "Finish Setup",
					showDesktopNext: false,
					showDesktopBack: false,
					showMobileBack: true,
					showMobileNext: true,
				};
			case 4:
				return {
					title: "Confirm Your Installations",
					labelBackStep: "Finish Step",
					labelNextStep: "",
					showDesktopBack: false,
					showMobileBack: false,
					showMobileNext: false,
					showDesktopNext: false,
				};
			default:
				return { title: "Add Appliance", labelNextStep: "Payment Information", labelBackStep: "" };
		}
	}, [currentStep]);

	const handleBack = useCallback(() => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	}, [currentStep]);

	const addAppliances = useCallback(
		(appliances: ApplianceItem[]) => {
			if (appliances.length === 0) {
				toast.warning("Select an appliance before proceeding");
				return;
			}
			setForm(new SolarRecommendationForm({ ...form, appliances: appliances }));

			if (!finishedSteps.includes(1)) {
				setFinishedSteps([...finishedSteps, 1]);
			}

			setCurrentStep(2);
		},
		[form, finishedSteps]
	);

	const addPaymentPlan = (paymentPlan: PaymentPlan) => {
		setForm(new SolarRecommendationForm({ ...form, paymentPlan: paymentPlan }));

		if (!finishedSteps.includes(2)) {
			setFinishedSteps([...finishedSteps, 2]);
		}

		setCurrentStep(3);
	};

	const addInstallation = (installation: Installation) => {
		setForm(new SolarRecommendationForm({ ...form, installation: installation }));
		setFinishedSteps([...finishedSteps, 3]);

		setCurrentStep(4);
	};

	function loadFinishedStepsFromForm(form: SolarRecommendationForm | undefined) {
		if (!form) return [];

		let steps: number[] = [];

		if (form.appliances && form.appliances.length > 0) steps.push(1);
		if (form.paymentPlan) steps.push(2);
		if (form.installation) steps.push(3);

		return steps;
	}

	useEffect(() => {
		sessionStorage.setItem("solar-recommendation-form", JSON.stringify(form));
	}, [form]);

	useEffect(() => {
		if (currentStep === 2) {
		}
		if (currentStep === 4) {
			finalize();
		}
	}, [currentStep, finalize]);

	return (
		<SolarRecommendationFormWrapper
			step={currentStep}
			onBack={handleBack}
			onNext={handleNext}
			subtitle=""
			title={wrapperObject.title}
			labelBackStep={wrapperObject.labelBackStep}
			labelNext={wrapperObject?.labelNext || "Save & Continue"}
			labelNextStep={wrapperObject.labelNextStep}
			showDesktopBack={wrapperObject.showDesktopBack}
			showMobileBack={wrapperObject.showMobileBack}
			showMobileNext={wrapperObject.showMobileNext}
			showDesktopNext={wrapperObject.showDesktopNext}
		>
			<div className={styles.contentContainer}>
				<div className={styles.pageContainer}>
					<div className={styles.formContainer}>
						{currentStep === 1 && <AddAppliancesForm ref={appliancesRef} initialAppliances={form.appliances} addAppliances={addAppliances} />}
						{currentStep === 2 && <PaymentPlanForm onBack={handleBack} ref={paymentRef} initialPaymentPlan={form.paymentPlan} addPaymentPlan={addPaymentPlan} />}
						{currentStep === 3 && <InstallationForm onBack={handleBack} initialInstallation={form.installation} addInstallation={addInstallation} />}
						{currentStep === 4 && <SolarRecommendationFinalization onBack={handleBack} />}
					</div>
				</div>
			</div>
		</SolarRecommendationFormWrapper>
	);
});

export default SolarRecommendation;
